import { zodResolver } from '@hookform/resolvers/zod'
import { Collapse, Modal, Popconfirm, Table } from 'antd'
import { Key, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import AtomTreeSelect from '@/components/Atoms/AtomTreeSelect/AtomTreeSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import Loader from '@/components/Atoms/Loader/Loader'
import ModalWithButtons from '@/components/Atoms/ModalWithButtons/ModalWithButtons'
import ToggleMenu from '@/components/Atoms/ToggleMenu/ToggleMenu'
import ExcelLikeTable from '@/components/NomenclatureImport/Excel/ExcelFilePreviewDataTable/ExcelFilePreviewDataTable'
import ExcelFileSheetsList from '@/components/NomenclatureImport/Excel/ExcelFileSheetsList/ExcelFileSheetsList'
import {
  ExcelFileUploaderContext,
  ExcelFileUploaderContextType,
} from '@/components/NomenclatureImport/Excel/ExcelFileUploader/ExcelFileUploader'
import FileUpload from '@/components/NomenclatureImport/FileUpload/FileUpload'
import { NULL_UUID_ID } from '@/constants'
import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import { useExcelFileUploaderStore } from '@/stores/import/excelFileUploaderStore'
import type {
  AntdTreeType,
  BrandInterface,
  CategoryInterface,
  CountryInterface,
  FormInputRegisterType,
  NomenclatureParsedFromExcelType,
  NomenclaturePreviewDataColumnMapping,
  NomenclatureType,
  OptionType,
  PluralNomenclatureImportResponseType,
  SelectFormControlType,
  TableCellType,
  TreeSelectFormControlType,
  UnitOfMeasurementInterface,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import {
  fetchBrands,
  fetchCategoriesTree,
  fetchCountries,
  fetchNomenclatureGroupsTree,
  fetchUnitsOfMeasurement,
} from '@/utils/api/fetch/fetchEntities'
import { filterApprovedCategoryTree } from '@/utils/catalog/approvalStatus'
import handleChangeBarcodeInput from '@/utils/forms/handleChangeBarcodeInput'
import handleChangeNumberInput from '@/utils/forms/handleChangeNumberInput'
import {
  cleanSupplierArticles,
  getSupplierArticlesError,
} from '@/utils/nomenclature/articles'
import { getBarcodeError, sanitizeBarcode } from '@/utils/nomenclature/barcode'
import getTableSelectedCellsRange from '@/utils/table/getTableSelectedCellsRange'
import convertToAntdFormatTree from '@/utils/tree/antd/convertToAntdFormatTree'
import handleSelectTreeNode from '@/utils/tree/selection/handleSelectTreeNode'

import EntitiesCreationDropdown from '../EntitiesCreationDropdown/EntitiesCreationDropdown'
import {
  getColumns,
  getNomenclatureCreatedTableColumns,
  getNomenclatureNotCreatedTableColumns,
  parsedDataPluralModificationSchema,
  type ParsedDataPluralModificationType,
} from './PluralImport.config'
import cn from './PluralImport.module.scss'

const NON_BULK_EDITABLE_COLUMNS = ['index', 'article', 'description']

function PluralImport() {
  const {
    file,
    includedSheets,
    allSheets,
    handleUploadFile,
    handleUploadPreviewData,
  } = useContext(ExcelFileUploaderContext) as ExcelFileUploaderContextType

  const [collapseActiveKey, setCollapseActiveKey] = useState<string>('')
  const [brands, setBrands] = useState<BrandInterface[]>([])
  const [countries, setCountries] = useState<CountryInterface[]>([])
  const [unitsOfMeasurement, setUnitsOfMeasurement] = useState<
    UnitOfMeasurementInterface[]
  >([])
  const [antdCategoriesTree, setAntdCategoriesTree] = useState<AntdTreeType[]>(
    []
  )
  const [antdNomenclatureGroupsTree, setAntdNomenclatureGroupsTree] = useState<
    AntdTreeType[]
  >([])

  const [parsedData, setParsedData] = useState<NomenclatureType[]>([])
  const [parsedDataLoading, setParsedDataLoading] = useState<boolean>(false)
  const [columnsKeys, setColumnsKeys] = useState<string[]>([])
  const [selectedCells, setSelectedCells] = useState<TableCellType[]>([])
  const [firstSelectedCell, setFirstSelectedCell] =
    useState<TableCellType | null>(null)
  const [oneCellSelected, setOneCellSelected] = useState<TableCellType>({
    rowKey: '',
    columnKey: '',
  })
  const [propertyInOneCellSelected, setPropertyInOneCellSelected] = useState<
    OptionType | string | null
  >(null)
  const [openParsedDataModificationModal, setOpenParsedDataModificationModal] =
    useState<boolean>(false)
  const [
    parsedDataModificationModalFields,
    setParsedDataModificationModalFields,
  ] = useState<string[]>([])
  const [categoryInFormSelectedPath, setCategoryInFormSelectedPath] =
    useState<string>('')
  const [
    nomenclatureGroupInFormSelectedPath,
    setNomenclatureGroupInFormSelectedPath,
  ] = useState<string>('')
  const [rowsWithErrors, setRowsWithErrors] = useState<string[]>([])
  const [nomenclatureCreationResponse, setNomenclatureCreationResponse] =
    useState<PluralNomenclatureImportResponseType[]>([])
  const [nomenclatureCreationLoading, setNomenclatureCreationLoading] =
    useState<boolean>(false)
  const [nomenclatureCreationErrorExists, setNomenclatureCreationErrorExists] =
    useState<boolean>(false)

  const isFetching = useRef({
    brands: false,
    countries: false,
    units: false,
    categories: false,
    groups: false,
  })

  const {
    categoriesTree,
    nomenclatureGroupsTree,
    setCategoriesTree,
    setNomenclatureGroupsTree,
  } = useTreeTilesStore()

  const {
    previewDataColumnMapping,
    currentIncludedSheet,
    setCurrentIncludedSheet,
  } = useExcelFileUploaderStore((state) => state)

  const selectableCategoriesTree = useMemo(
    () => filterApprovedCategoryTree(categoriesTree),
    [categoriesTree]
  )

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ParsedDataPluralModificationType>({
    resolver: zodResolver(parsedDataPluralModificationSchema),
  })

  const [modal, contextHolder] = Modal.useModal()

  const forceFetchBrands = async () => {
    try {
      const data = await fetchBrands()
      setBrands(data)
    } catch (error) {
      toast.error('Ошибка загрузки брендов')
    }
  }

  const handleBrandsHover = async () => {
    if (brands.length || isFetching.current.brands) return
    isFetching.current.brands = true
    await forceFetchBrands()
    isFetching.current.brands = false
  }

  const handleCountriesHover = async () => {
    if (countries.length || isFetching.current.countries) return
    isFetching.current.countries = true
    try {
      const data = await fetchCountries()
      setCountries(data)
    } finally {
      isFetching.current.countries = false
    }
  }

  const handleUnitsHover = async () => {
    if (unitsOfMeasurement.length || isFetching.current.units) return
    isFetching.current.units = true
    try {
      const data = await fetchUnitsOfMeasurement()
      setUnitsOfMeasurement(data)
    } finally {
      isFetching.current.units = false
    }
  }

  const forceFetchCategories = async () => {
    try {
      const data = await fetchCategoriesTree()
      setCategoriesTree(data)
    } catch (error) {
      toast.error('Ошибка загрузки категорий')
    }
  }

  const handleCategoriesHover = async () => {
    if (categoriesTree.length || isFetching.current.categories) return
    isFetching.current.categories = true
    await forceFetchCategories()
    isFetching.current.categories = false
  }

  const forceFetchGroups = async () => {
    try {
      const data = await fetchNomenclatureGroupsTree()
      setNomenclatureGroupsTree(data)
    } catch (error) {
      toast.error('Ошибка загрузки групп номенклатуры')
    }
  }

  const handleNomenclatureGroupsHover = async () => {
    if (nomenclatureGroupsTree.length || isFetching.current.groups) return
    isFetching.current.groups = true
    await forceFetchGroups()
    isFetching.current.groups = false
  }

  const handleClickCell = (
    rowKey: string,
    columnKey: string,
    e: React.MouseEvent
  ) => {
    if (columnKey === 'brand') handleBrandsHover()
    if (columnKey === 'category') handleCategoriesHover()
    if (columnKey === 'nomenclatureGroup') handleNomenclatureGroupsHover()
    if (columnKey === 'countryOfOrigin') handleCountriesHover()
    if (columnKey === 'unitOfMeasurement') handleUnitsHover()

    if (e.altKey && !e.shiftKey) {
      setSelectedCells((prev) =>
        prev.filter(
          (cell) => !(cell.columnKey === columnKey && cell.rowKey === rowKey)
        )
      )
      return
    }

    if (!e.shiftKey && !e.altKey) {
      setFirstSelectedCell({ rowKey, columnKey } as TableCellType)
      setSelectedCells([{ rowKey, columnKey } as TableCellType])
      setOneCellSelected({ rowKey, columnKey } as TableCellType)
      const record = parsedData.find((p) => p.key === rowKey)
      setPropertyInOneCellSelected(
        record ? (record[columnKey] as OptionType) : null
      )
    }

    if (firstSelectedCell && e.shiftKey) {
      const columns = getColumns({
        selectedCells,
        brands,
        countries,
        unitsOfMeasurement,
        categoriesTree,
        antdCategoriesTree,
        nomenclatureGroupsTree,
        antdNomenclatureGroupsTree,
        parsedData,
        oneCellSelected,
        propertyInOneCellSelected,
        setPropertyInOneCellSelected,
        handleClickCell,
        setParsedData,
        removeTableRow,
      })

      const newSelectedCells = getTableSelectedCellsRange(
        parsedData,
        columns,
        firstSelectedCell,
        { rowKey, columnKey }
      )
      setSelectedCells(newSelectedCells)

      if (newSelectedCells.length > 1) {
        const fields: string[] = []
        newSelectedCells.forEach((cell) => {
          if (
            !fields.includes(cell.columnKey) &&
            columnsKeys.includes(cell.columnKey) &&
            !NON_BULK_EDITABLE_COLUMNS.includes(cell.columnKey)
          ) {
            fields.push(cell.columnKey)
          }
        })

        if (fields.length) {
          setOpenParsedDataModificationModal(true)
          setOneCellSelected({ rowKey: '', columnKey: '' })
          setParsedDataModificationModalFields(fields)
        } else {
          setSelectedCells([])
          setOneCellSelected({ rowKey, columnKey })
        }
      } else {
        setOneCellSelected({ rowKey, columnKey })
      }
    }
  }

  const parseExcelFileByColumnMapping = (params: {
    brandsFetched: BrandInterface[]
    countriesFetched: CountryInterface[]
    unitsOfMeasurementFetched: UnitOfMeasurementInterface[]
  }) => {
    const { brandsFetched, unitsOfMeasurementFetched, countriesFetched } =
      params

    const brandLookup = new Map(
      brandsFetched.map((b) => [b.description.toLowerCase().trim(), b])
    )
    const unitLookup = new Map(
      unitsOfMeasurementFetched.map((u) => [
        u.description.toLowerCase().trim(),
        u,
      ])
    )
    const countryLookup = new Map(
      countriesFetched.map((c) => [c.description.toLowerCase().trim(), c])
    )

    const loadingToast = toast.loading('Парсинг Excel-файла...')

    const sheetsToProcess = includedSheets.length ? includedSheets : allSheets

    const formatNomenclature = (
      nomenclature: NomenclatureParsedFromExcelType,
      selectablePropValue: { value: string; label: string }
    ): NomenclatureType => {
      let matchedBrand = { ...selectablePropValue }
      let rawBrand = ''
      if (nomenclature.brand) {
        rawBrand = String(nomenclature.brand).trim()
        const foundBrand = brandLookup.get(rawBrand.toLowerCase())
        if (foundBrand) {
          matchedBrand = { value: foundBrand.id, label: foundBrand.description }
          rawBrand = ''
        }
      }

      let matchedUnit = { ...selectablePropValue }
      let rawUnitOfMeasurement = ''
      if (nomenclature.unitOfMeasurement) {
        rawUnitOfMeasurement = String(nomenclature.unitOfMeasurement).trim()
        const foundUnit = unitLookup.get(
          rawUnitOfMeasurement.toLowerCase().replace('.', '')
        )
        if (foundUnit) {
          matchedUnit = { value: foundUnit.id, label: foundUnit.description }
          rawUnitOfMeasurement = ''
        }
      }

      let matchedCountry = { ...selectablePropValue }
      if (nomenclature.countryOfOrigin) {
        const rawCountry = String(nomenclature.countryOfOrigin)
          .toLowerCase()
          .trim()
        const foundCountry = countryLookup.get(rawCountry)
        if (foundCountry) {
          matchedCountry = {
            value: foundCountry.id,
            label: foundCountry.description,
          }
        }
      }

      let matchedCategory = { ...selectablePropValue }
      if (nomenclature.category) {
        const rawCategory = String(nomenclature.category).toLowerCase().trim()
        const findInTree = (
          nodes: CategoryInterface[]
        ): CategoryInterface['category'] | undefined => {
          for (const node of nodes) {
            if (node.category.description.toLowerCase().trim() === rawCategory)
              return node.category
            const found = findInTree(node.childCategories)
            if (found) return found
          }
          return undefined
        }
        const foundCategory = findInTree(selectableCategoriesTree)
        if (foundCategory) {
          matchedCategory = {
            value: foundCategory.id,
            label: foundCategory.description,
          }
        }
      }

      return {
        article: String(nomenclature.article || ''),
        supplierArticle: nomenclature.supplierArticle || '',
        description: nomenclature.description,
        barcode: sanitizeBarcode(nomenclature.barcode || ''),
        itemLength: String(nomenclature.itemLength || ''),
        itemWidth: String(nomenclature.itemWidth || ''),
        itemHeight: String(nomenclature.itemHeight || ''),
        itemWeight: String(nomenclature.itemWeight || ''),
        warranty: String(nomenclature.warranty || ''),
        details: nomenclature.details || '',
        comment: '',
        unitOfMeasurement: matchedUnit,
        countryOfOrigin: matchedCountry,
        brand: matchedBrand,
        rawBrand,
        rawUnitOfMeasurement,
        category: matchedCategory,
        nomenclatureGroup: { ...selectablePropValue },
        mainImage: { ...selectablePropValue },
        key: `${nomenclature.article} ${nomenclature.description} ${Math.random()}`,
      }
    }

    const allFormattedNomenclature: NomenclatureType[] = []
    const allValidColumnMappingKeys = new Set<string>()

    const sheetRequests = sheetsToProcess.map((sheet) => {
      const currentSheetMapping = previewDataColumnMapping[sheet]
      const validColumnMappings: Record<string, number> = {}

      Object.keys(currentSheetMapping).forEach((key) => {
        const mappingKey = key as keyof NomenclaturePreviewDataColumnMapping
        const value = currentSheetMapping[mappingKey]
        if (value !== undefined && value !== -1) {
          validColumnMappings[key] = value
        }
      })

      const sheetFormData = new FormData()
      if (file != null) {
        sheetFormData.append('excelFile', file)
      }
      sheetFormData.append(
        'columnNumbersStringJson',
        JSON.stringify(validColumnMappings)
      )

      return api
        .post('/excel-parser/nomenclature/preview', sheetFormData, {
          params: { sourceSheetName: sheet },
        })
        .then((response) => {
          const nomenclatureParsedFromExcel: NomenclatureParsedFromExcelType[] =
            response.data
          const selectablePropValue = { value: '', label: '' }

          nomenclatureParsedFromExcel.forEach((nomenclature) => {
            allFormattedNomenclature.push(
              formatNomenclature(nomenclature, selectablePropValue)
            )
          })

          Object.keys(validColumnMappings).forEach((k) =>
            allValidColumnMappingKeys.add(k)
          )
        })
    })

    Promise.all(sheetRequests)
      .then(() => {
        toast.dismiss(loadingToast)
        toast.success('Данные загружены', {
          description: `Обработано ${allFormattedNomenclature.length} товаров`,
        })

        const mappedKeysFromExcel = [...allValidColumnMappingKeys].map((k) =>
          k
            .replace('ColumnNumber', '')
            .replace('articleInch', 'supplierArticle')
        )

        setColumnsKeys([
          'index',
          'article',
          'description',
          'brand',
          'category',
          'nomenclatureGroup',
          ...mappedKeysFromExcel.filter(
            (k) =>
              ![
                'article',
                'description',
                'brand',
                'category',
                'nomenclatureGroup',
              ].includes(k)
          ),
          'removalAction',
        ])

        setParsedData(allFormattedNomenclature)
      })
      .catch((error) => {
        toast.dismiss(loadingToast)
        handleApiError(error, modal)
      })
      .finally(() => {
        setParsedDataLoading(false)
      })
  }

  const handleChangeParsedData = (data: ParsedDataPluralModificationType) => {
    const modificationData: Record<string, unknown> & {
      multiplicity?: string
    } = data
    const newParsedData = [...parsedData]

    newParsedData.forEach((item) => {
      const isRowSelected = selectedCells.some(
        (cell) => cell.rowKey === item.key
      )

      if (isRowSelected) {
        parsedDataModificationModalFields.forEach((field) => {
          if (field === 'brand' && data.brand) {
            item.brand = { value: data.brand.value, label: data.brand.label }
          }
          if (field === 'category' && data.category) {
            const names = categoryInFormSelectedPath?.split(' > ')
            item.category = {
              value: data.category,
              label: names ? names[names.length - 1] : '',
            }
          }
          if (field === 'nomenclatureGroup' && data.nomenclatureGroup) {
            const names = nomenclatureGroupInFormSelectedPath?.split(' > ')
            item.nomenclatureGroup = {
              value: data.nomenclatureGroup,
              label: names ? names[names.length - 1] : '',
            }
          }
          if (field === 'countryOfOrigin' && data.countryOfOrigin) {
            item.countryOfOrigin = {
              value: data.countryOfOrigin.value,
              label: data.countryOfOrigin.label,
            }
          }
          if (field === 'unitOfMeasurement' && data.unitOfMeasurement) {
            item.unitOfMeasurement = {
              value: data.unitOfMeasurement.value,
              label: data.unitOfMeasurement.label,
            }
          }

          // Логика для строковых полей
          if (field === 'barcode' && data.barcode) item.barcode = data.barcode
          if (field === 'details' && data.details) item.details = data.details
          if (field === 'comment' && data.comment) item.comment = data.comment
          if (field === 'multiplicity' && modificationData.multiplicity)
            item.multiplicity = modificationData.multiplicity

          // Логика для числовых полей (убираем лишние нули в начале)
          const numFields = [
            'itemLength',
            'itemWidth',
            'itemHeight',
            'itemWeight',
            'warranty',
          ]
          if (numFields.includes(field)) {
            const val = modificationData[field]
            if (typeof val === 'string' && val) {
              item[field] =
                val.startsWith('0') && !val.includes('.') ? val.slice(1) : val
            }
          }
        })
      }
    })

    setParsedData(newParsedData)
    setSelectedCells([])
    setOpenParsedDataModificationModal(false)
  }

  const handleGoToFourthStep = async () => {
    const isPreviewDataColumnMappingNotFull = Object.keys(
      previewDataColumnMapping
    ).some((sheet) => {
      return (
        previewDataColumnMapping[sheet].articleColumnNumber == -1 ||
        previewDataColumnMapping[sheet].descriptionColumnNumber == -1
      )
    })

    if (isPreviewDataColumnMappingNotFull) {
      toast.warning('Заполните обязательные поля (Артикул и Наименование)')
      return
    }

    setCollapseActiveKey('fourthStep')
    setParsedDataLoading(true)

    try {
      const currentSheetMapping =
        previewDataColumnMapping[currentIncludedSheet || allSheets[0]]
      const hasBrandMapping = currentSheetMapping.brandColumnNumber !== -1
      const hasUnitMapping =
        currentSheetMapping.unitOfMeasurementColumnNumber !== -1

      const [brandsFetched, unitsFetched] = await Promise.all([
        hasBrandMapping ? fetchBrands() : Promise.resolve(brands),
        hasUnitMapping
          ? fetchUnitsOfMeasurement()
          : Promise.resolve(unitsOfMeasurement),
      ])

      setBrands(brandsFetched)
      setUnitsOfMeasurement(unitsFetched)

      parseExcelFileByColumnMapping({
        brandsFetched,
        countriesFetched: countries,
        unitsOfMeasurementFetched: unitsFetched,
      })
    } catch (error) {
      setParsedDataLoading(false)
      toast.error('Ошибка при подготовке данных')
    }
  }

  const removeTableRow = (rowKey: Key) => {
    const newParsedData = parsedData.filter(
      (nomenclature) => nomenclature.key != rowKey
    )
    setParsedData(newParsedData)
    toast.info('Строка удалена')
  }

  const handleImportNomenclature = () => {
    setNomenclatureCreationLoading(true)
    setCollapseActiveKey('result')
    const newRowsWithErrors: string[] = []

    const loadingToast = toast.loading('Создание номенклатуры...', {
      description: `Обработка ${parsedData.length} товаров`,
    })

    const formattedParsedData = parsedData.map((nomenclature) => {
      if (
        !nomenclature.nomenclatureGroup.value ||
        !nomenclature.category.value ||
        !nomenclature.brand.value ||
        !nomenclature.article ||
        !nomenclature.description ||
        getBarcodeError(String(nomenclature.barcode ?? '')) ||
        getSupplierArticlesError([String(nomenclature.supplierArticle ?? '')])
      ) {
        newRowsWithErrors.push(String(nomenclature.key))
      }

      const getVal = (field: unknown): string => {
        if (field && typeof field === 'object' && 'value' in field) {
          return String(field.value)
        }
        return ''
      }

      const safeNum = (v: unknown) => {
        const n = parseFloat(String(v).replace(',', '.'))
        return isNaN(n) ? 0 : n
      }

      return {
        // ОБЯЗАТЕЛЬНЫЕ
        parentId: getVal(nomenclature.nomenclatureGroup) || NULL_UUID_ID, // UUID
        description: String(nomenclature.description),
        article: String(nomenclature.article).trim(),
        brand: getVal(nomenclature.brand), // UUID
        category: getVal(nomenclature.category), // UUID

        // НЕОБЯЗАТЕЛЬНЫЕ
        supplierArticles: cleanSupplierArticles([
          String(nomenclature.supplierArticle ?? ''),
        ]),
        barcode: sanitizeBarcode(String(nomenclature.barcode ?? '')),
        countryOfOrigin: nomenclature.countryOfOrigin?.value || undefined, // UUID
        unitOfMeasurement: nomenclature.unitOfMeasurement?.value || undefined, // UUID
        itemLength: safeNum(nomenclature.itemLength),
        itemWidth: safeNum(nomenclature.itemWidth),
        itemHeight: safeNum(nomenclature.itemHeight),
        itemWeight: safeNum(nomenclature.itemWeight),
        warranty: parseInt(String(nomenclature.warranty)) || 0,
        details: nomenclature.details || '',
        comment: nomenclature.comment || '',
        mainImage: getVal(nomenclature.mainImage) || NULL_UUID_ID,
      }
    })

    if (newRowsWithErrors.length) {
      toast.dismiss(loadingToast)
      setCollapseActiveKey('fourthStep')
      setRowsWithErrors(newRowsWithErrors)
      toast.warning('Исправьте данные в таблице', {
        description: `Не заполнены обязательные поля или некорректны штрихкод и артикул поставщика, строк: ${newRowsWithErrors.length}`,
      })
      setNomenclatureCreationLoading(false)
      return
    }

    api
      .post('main/nomenclatures/batch', { requests: formattedParsedData })
      .then((response) => {
        toast.dismiss(loadingToast)
        const results: PluralNomenclatureImportResponseType[] =
          response.data.results || response.data

        const successCount = results.filter(
          (r) => r.operationStatus === 'SUCCESS'
        ).length

        const failures = results.filter((r) => r.operationStatus === 'FAILURE')

        const failureCount = failures.length

        if (successCount > 0) {
          toast.success('Импорт завершен', {
            description: `Создано товаров: ${successCount}`,
          })
        }

        if (failureCount > 0) {
          const errorSummary = failures
            .slice(0, 3)
            .map((f) => `• ${f.errorMessage}`)
            .join('\n')

          const footer =
            failureCount > 3 ? `\n...и еще ${failureCount - 3} ошибок` : ''

          toast.error(`Ошибки при создании (${failureCount})`, {
            description: (
              <div className={cn.creationErrorsToast}>
                {errorSummary}
                {footer}
                <div className={cn.creationErrorsHint}>
                  Подробности в таблице ниже
                </div>
              </div>
            ),
            duration: 25000,
          })
        }

        setNomenclatureCreationErrorExists(false)
        setNomenclatureCreationLoading(false)
        setNomenclatureCreationResponse(results)
      })
      .catch((error) => {
        toast.error('Ошибка сервера', {
          id: loadingToast,
          description:
            error.response?.data?.message || 'Не удалось создать номенклатуру',
        })
        setNomenclatureCreationResponse([])
        setNomenclatureCreationLoading(false)
        setNomenclatureCreationErrorExists(true)
      })
  }

  useEffect(() => {
    if (categoriesTree.length) {
      setAntdCategoriesTree(
        convertToAntdFormatTree({
          nodes: categoriesTree,
          classNames: cn,
          allSelectable: false,
          inTreeSelect: true,
        })
      )
    }
  }, [categoriesTree])

  useEffect(() => {
    if (nomenclatureGroupsTree.length) {
      setAntdNomenclatureGroupsTree(
        convertToAntdFormatTree({
          nodes: nomenclatureGroupsTree,
          classNames: cn,
          allSelectable: false,
          inTreeSelect: true,
        })
      )
    }
  }, [nomenclatureGroupsTree])

  return (
    <>
      {contextHolder}
      <div className={cn.collapseContainer}>
        <Collapse
          accordion
          defaultActiveKey="firstStep"
          activeKey={collapseActiveKey}
          onChange={(panelKey) => {
            const setActiveKey = () => setCollapseActiveKey(panelKey[0])
            const isPreviewDataColumnMappingNotFull = Object.keys(
              previewDataColumnMapping
            ).length
              ? Object.keys(previewDataColumnMapping).some((sheet) => {
                  if (
                    previewDataColumnMapping[sheet].articleColumnNumber == -1 ||
                    previewDataColumnMapping[sheet].descriptionColumnNumber ==
                      -1
                  ) {
                    return true
                  }
                })
              : true
            if (panelKey[0] == 'firstStep') {
              setActiveKey()
              return
            } else if (
              (panelKey[0] == 'thirdStep' || panelKey[0] == 'secondStep') &&
              file instanceof File
            ) {
              setActiveKey()
            } else if (
              !isPreviewDataColumnMappingNotFull &&
              panelKey[0] == 'fourthStep'
            ) {
              setActiveKey()
            } else if (
              nomenclatureCreationResponse.length &&
              panelKey[0] == 'result'
            ) {
              setActiveKey()
            } else {
              setCollapseActiveKey('')
            }
          }}
          items={[
            {
              key: 'firstStep',
              label: 'Шаг 1: Загрузка Excel-файла',
              children: (
                <div className={cn.firstStep}>
                  <FileUpload
                    file={file}
                    inputId="nomenclature-import-plural-excel-file-upload"
                    accept=".xlsx, .xls"
                    handleUploadFile={handleUploadFile}
                  />
                  <button
                    onClick={() => {
                      if (file instanceof File) {
                        setCollapseActiveKey('secondStep')
                      } else {
                        toast.warning('Сначала загрузите файл')
                      }
                    }}
                    className={cn.toNextStepButton}
                  >
                    Далее
                  </button>
                </div>
              ),
            },
            {
              key: 'secondStep',
              label: 'Шаг 2: Выбор листов',
              children: (
                <div className={cn.secondStep}>
                  <ExcelFileSheetsList />
                  <button
                    onClick={() => {
                      handleUploadPreviewData()
                      setCollapseActiveKey('thirdStep')
                    }}
                    className={cn.toNextStepButton}
                  >
                    Далее
                  </button>
                </div>
              ),
            },
            {
              key: 'thirdStep',
              label: 'Шаг 3: Выбор соответствия колонок',
              children: (
                <div className={cn.thirdStep}>
                  {includedSheets.length && (
                    <ToggleMenu
                      menuItems={includedSheets.map((sheet, index) => ({
                        id: index,
                        name: sheet,
                      }))}
                      current={
                        currentIncludedSheet
                          ? currentIncludedSheet
                          : includedSheets[0]
                      }
                      setCurrent={setCurrentIncludedSheet}
                    />
                  )}
                  <ExcelLikeTable />
                  {parsedData.length ? (
                    <Popconfirm
                      title="Изменение данных"
                      description="Вы действительно хотите еще раз загрузить данные? Старые данные будут заменены."
                      onConfirm={handleGoToFourthStep}
                      onCancel={() => null}
                      okText="Да"
                      cancelText="Нет"
                      overlayClassName={cn.popconfirm}
                      align={{
                        offset: [48, -16],
                      }}
                      icon={null}
                    >
                      <button className={cn.toNextStepButton}>Далее</button>
                    </Popconfirm>
                  ) : (
                    <button
                      className={cn.toNextStepButton}
                      onClick={() => {
                        setParsedDataLoading(true)
                        handleGoToFourthStep()
                      }}
                    >
                      Далее
                    </button>
                  )}
                </div>
              ),
            },
            {
              key: 'fourthStep',
              label: 'Шаг 4: Корректировка',
              children: (
                <>
                  {parsedData.length && !parsedDataLoading ? (
                    <div className={cn.fourthStep}>
                      {/* --- БЛОК ИНСТРУКЦИИ --- */}
                      <div className={cn.instructionBlock}>
                        <div className={cn.instructionHeader}>
                          💡 Как быстро заполнить таблицу:
                        </div>
                        <div className={cn.instructionGrid}>
                          <div className={cn.instructionStep}>
                            <b>Массовое изменение:</b> Кликните на ячейку
                            (начало), затем зажмите <b>Shift</b> и кликните на
                            другую ячейку (конец). Появится окно для заполнения
                            всего диапазона сразу.
                          </div>
                          <div className={cn.instructionStep}>
                            <b>Дополнительные колонки:</b> Если вам нужны поля
                            «Вес», «Гарантия» или «Страна», добавьте их через
                            кнопку <b>«+»</b> (слева над таблицей).
                          </div>
                        </div>
                      </div>
                      {/* ---------------------------- */}
                      <div className={cn.entitiesCreationDropdown}>
                        <EntitiesCreationDropdown
                          onBrandCreated={forceFetchBrands}
                          onCategoryCreated={forceFetchCategories}
                          onGroupCreated={forceFetchGroups}
                          columnsKeys={columnsKeys}
                          setColumnsKeys={setColumnsKeys}
                        />
                      </div>
                      <div className={cn.parsedDataTable}>
                        <ModalWithButtons
                          open={openParsedDataModificationModal}
                          title="Установление значений свойств"
                          onOk={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleSubmit((data) => {
                              handleChangeParsedData(data)
                              setOpenParsedDataModificationModal(false)
                            })()
                          }}
                          onCancel={() => {
                            setOpenParsedDataModificationModal(false)
                            setSelectedCells([])
                          }}
                        >
                          <form className={cn.parsedDataPluralModificationForm}>
                            {parsedDataModificationModalFields.includes(
                              'brand'
                            ) && (
                              <div onMouseEnter={handleBrandsHover}>
                                <HoverElement>
                                  <AtomSelect
                                    title="Бренд:"
                                    openByHover={false}
                                    formControl={
                                      control as SelectFormControlType
                                    }
                                    formName="brand"
                                    options={brands.map((brand) => ({
                                      value: brand.id,
                                      label: brand.description,
                                    }))}
                                    showSearch
                                  />
                                </HoverElement>
                              </div>
                            )}
                            {parsedDataModificationModalFields.includes(
                              'category'
                            ) && (
                              <div onMouseEnter={handleCategoriesHover}>
                                <HoverElement>
                                  <AtomTreeSelect
                                    title="Категория:"
                                    openByHover={false}
                                    tree={selectableCategoriesTree}
                                    antdTree={antdCategoriesTree}
                                    handleChange={(tree, node) => {
                                      const pathToCategory =
                                        handleSelectTreeNode(tree, node)
                                      if (pathToCategory) {
                                        setCategoryInFormSelectedPath(
                                          pathToCategory
                                        )
                                      }
                                    }}
                                    formName="category"
                                    formControl={
                                      control as TreeSelectFormControlType
                                    }
                                    handleClear={() => {}}
                                  />
                                </HoverElement>
                              </div>
                            )}
                            {parsedDataModificationModalFields.includes(
                              'nomenclatureGroup'
                            ) && (
                              <div onMouseEnter={handleNomenclatureGroupsHover}>
                                <HoverElement>
                                  <AtomTreeSelect
                                    title="Группа номенклатуры:"
                                    openByHover={false}
                                    tree={nomenclatureGroupsTree}
                                    antdTree={antdNomenclatureGroupsTree}
                                    handleChange={(tree, node) => {
                                      const pathToNomenclatureGroup =
                                        handleSelectTreeNode(tree, node)
                                      if (pathToNomenclatureGroup) {
                                        setNomenclatureGroupInFormSelectedPath(
                                          pathToNomenclatureGroup
                                        )
                                      }
                                    }}
                                    formName="nomenclatureGroup"
                                    formControl={
                                      control as TreeSelectFormControlType
                                    }
                                    handleClear={() => {}}
                                  />
                                </HoverElement>
                              </div>
                            )}
                            {parsedDataModificationModalFields.includes(
                              'barcode'
                            ) && (
                              <AtomFormInput
                                name="barcode"
                                location="parsedNomenclatureDataModification"
                                title="Штрихкод:"
                                errors={errors}
                                handleChangeInput={handleChangeBarcodeInput}
                                register={register as FormInputRegisterType}
                              />
                            )}
                            {parsedDataModificationModalFields.includes(
                              'countryOfOrigin'
                            ) && (
                              <div onMouseEnter={handleCountriesHover}>
                                <HoverElement>
                                  <AtomSelect
                                    title="Страна происхождения:"
                                    openByHover={false}
                                    formControl={
                                      control as SelectFormControlType
                                    }
                                    formName="countryOfOrigin"
                                    options={countries.map((country) => ({
                                      value: country.id,
                                      label: country.description,
                                    }))}
                                    showSearch
                                  />
                                </HoverElement>
                              </div>
                            )}
                            {parsedDataModificationModalFields.includes(
                              'itemLength'
                            ) && (
                              <AtomFormInput
                                name="itemLength"
                                location="parsedNomenclatureDataModification"
                                title="Длина, см:"
                                handleChangeInput={(e) => {
                                  handleChangeNumberInput(e, true)
                                }}
                                register={register as FormInputRegisterType}
                              />
                            )}
                            {parsedDataModificationModalFields.includes(
                              'itemWidth'
                            ) && (
                              <AtomFormInput
                                name="itemWidth"
                                location="parsedNomenclatureDataModification"
                                title="Ширина, см:"
                                handleChangeInput={(e) => {
                                  handleChangeNumberInput(e, true)
                                }}
                                register={register as FormInputRegisterType}
                              />
                            )}
                            {parsedDataModificationModalFields.includes(
                              'itemHeight'
                            ) && (
                              <AtomFormInput
                                name="itemHeight"
                                location="parsedNomenclatureDataModification"
                                title="Высота, см:"
                                handleChangeInput={(e) => {
                                  handleChangeNumberInput(e, true)
                                }}
                                register={register as FormInputRegisterType}
                              />
                            )}
                            {parsedDataModificationModalFields.includes(
                              'itemWeight'
                            ) && (
                              <AtomFormInput
                                name="itemWeight"
                                location="parsedNomenclatureDataModification"
                                title="Вес, кг:"
                                handleChangeInput={(e) => {
                                  handleChangeNumberInput(e, true)
                                }}
                                register={register as FormInputRegisterType}
                              />
                            )}
                            {parsedDataModificationModalFields.includes(
                              'warranty'
                            ) && (
                              <AtomFormInput
                                name="warranty"
                                location="parsedNomenclatureDataModification"
                                title="Гарантия, мес.:"
                                handleChangeInput={(e) => {
                                  handleChangeNumberInput(e, false)
                                }}
                                register={register as FormInputRegisterType}
                              />
                            )}
                            {parsedDataModificationModalFields.includes(
                              'unitOfMeasurement'
                            ) && (
                              <div onMouseEnter={handleUnitsHover}>
                                <HoverElement>
                                  <AtomSelect
                                    title="Единица измерения:"
                                    openByHover={false}
                                    formControl={
                                      control as SelectFormControlType
                                    }
                                    formName="unitOfMeasurement"
                                    options={unitsOfMeasurement.map(
                                      (unitOfMeasurement) => ({
                                        value: unitOfMeasurement.id,
                                        label: unitOfMeasurement.description,
                                      })
                                    )}
                                    showSearch
                                  />
                                </HoverElement>
                              </div>
                            )}
                            {parsedDataModificationModalFields.includes(
                              'details'
                            ) && (
                              <div className={cn.formItemWithTextarea}>
                                <label>Описание:</label>
                                <textarea {...register('details')}></textarea>
                              </div>
                            )}
                            {parsedDataModificationModalFields.includes(
                              'comment'
                            ) && (
                              <div className={cn.formItemWithTextarea}>
                                <label>Комментарий:</label>
                                <textarea {...register('comment')}></textarea>
                              </div>
                            )}
                          </form>
                        </ModalWithButtons>
                        <Table
                          size="small"
                          columns={getColumns({
                            selectedCells,
                            brands,
                            countries,
                            unitsOfMeasurement,
                            categoriesTree,
                            antdCategoriesTree,
                            nomenclatureGroupsTree,
                            antdNomenclatureGroupsTree,
                            parsedData,
                            oneCellSelected,
                            propertyInOneCellSelected,
                            setPropertyInOneCellSelected,
                            handleClickCell,
                            setParsedData,
                            removeTableRow,
                          }).filter((column) =>
                            columnsKeys.includes(String(column.key))
                          )}
                          dataSource={parsedData}
                          rowKey="key"
                          rowClassName={(record) => {
                            return rowsWithErrors.includes(String(record.key))
                              ? cn.withErrors
                              : ''
                          }}
                          pagination={false}
                          scroll={{ x: 'max-content', y: 7 * 80 }}
                        />
                      </div>
                      <button
                        className={cn.toNextStepButton}
                        onClick={handleImportNomenclature}
                      >
                        Загрузить
                      </button>
                    </div>
                  ) : parsedDataLoading ? (
                    <Loader />
                  ) : (
                    <p className={cn.emptyParsedDataTableMessage}>
                      Не удалось сформировать таблицу
                    </p>
                  )}
                </>
              ),
            },
            {
              key: 'result',
              label: 'Шаг 5: Результат',
              children: (
                <>
                  {!!nomenclatureCreationLoading && <Loader />}
                  {!!nomenclatureCreationErrorExists && (
                    <div className={cn.failureResult}>
                      <span className={cn.errorMessage}>
                        Произошла ошибка при создании номенклатуры
                      </span>
                    </div>
                  )}
                  {!nomenclatureCreationLoading &&
                    !nomenclatureCreationErrorExists && (
                      <div className={cn.successResult}>
                        <span>
                          По вашему запросу создана номенклатура{' '}
                          {
                            nomenclatureCreationResponse.filter(
                              (nomenclatureItem) =>
                                nomenclatureItem.operationStatus == 'SUCCESS'
                            ).length
                          }{' '}
                          товаров:
                        </span>
                        <div className={cn.nomenclatureCreatedTable}>
                          <Table
                            dataSource={nomenclatureCreationResponse.filter(
                              (nomenclatureItem) =>
                                nomenclatureItem.operationStatus === 'SUCCESS'
                            )}
                            columns={getNomenclatureCreatedTableColumns({
                              categoriesTree,
                              antdCategoriesTree,
                              nomenclatureGroupsTree,
                              antdNomenclatureGroupsTree,
                            })}
                            pagination={false}
                            rowKey={(record) =>
                              record.createdNomenclature?.id || Math.random()
                            }
                          />
                        </div>
                      </div>
                    )}
                  {!nomenclatureCreationLoading &&
                    !nomenclatureCreationErrorExists &&
                    nomenclatureCreationResponse.filter(
                      (item) => item.operationStatus === 'FAILURE'
                    ).length > 0 && (
                      <div className={cn.successResult}>
                        <span>
                          Не создана номенклатура{' '}
                          {
                            nomenclatureCreationResponse.filter(
                              (nomenclatureItem) =>
                                nomenclatureItem.operationStatus == 'FAILURE'
                            ).length
                          }{' '}
                          товаров:
                        </span>
                        <div className={cn.nomenclatureCreatedTable}>
                          <Table
                            dataSource={nomenclatureCreationResponse
                              .filter(
                                (item) => item.operationStatus === 'FAILURE'
                              )
                              .map((item) => ({
                                article: item.nomenclatureIdentifier.article,
                                brand:
                                  brands.find(
                                    (b) =>
                                      b.id ===
                                      item.nomenclatureIdentifier.brandId
                                  )?.description || 'Неизвестен',
                                errorMessage: item.errorMessage,
                              }))}
                            columns={getNomenclatureNotCreatedTableColumns()}
                            pagination={false}
                            rowKey={(record) => record.article + record.brand}
                          />
                        </div>
                      </div>
                    )}
                </>
              ),
            },
          ]}
        />
      </div>
    </>
  )
}

export default PluralImport
